import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import {
  AccountCircleOutlined,
  LightModeRounded,
  DarkModeRounded,
  TextDecreaseRounded,
  TextIncreaseRounded,
} from '@mui/icons-material'
import { Menu, MenuItem, Typography, Link, useTheme, Stack, Box, Slider } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import {
  logoutUser,
  changeCurrentTheme,
  changeCurrentLanguage,
  askForAccess,
  setHtmlFontSize,
} from '../../store/actions'

const FsAccountMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { token, account, currentTheme, currentLanguage, htmlFontSize } = useAppSelector(
    (state) => state.currentUser,
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const theme = useTheme()

  const handleLogout = () => {
    dispatch(logoutUser())
    navigate('/login')
  }

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const navigateToImportView = () => {
    if (token && account?.isSuperuser && account?.profile.appAccess?.includes('fs'))
      navigate('/fs/admin/import')
    else {
      dispatch(askForAccess('fs'))
    }
  }
  const navigateToConfigurationView = () => {
    if (token && account?.isSuperuser && account?.profile.appAccess?.includes('fs'))
      navigate('/fs/admin/config')
    else {
      dispatch(askForAccess('fs'))
    }
  }
  const navigateToPtImportView = () => {
    if (token && account?.isSuperuser && account?.profile.appAccess?.includes('pt'))
      navigate('/pt/admin/import')
    else {
      dispatch(askForAccess('pt'))
    }
  }

  const switchLanguage = (l: 'de' | 'en') => {
    i18n.changeLanguage(l)
    document.documentElement.setAttribute('lang', l)
    dispatch(changeCurrentLanguage(l))
  }

  useEffect(() => {
    i18n.changeLanguage(currentLanguage)
    document.documentElement.setAttribute('lang', currentLanguage)
  }, [])

  const switchTheme = (mode: 'light' | 'dark') => {
    dispatch(changeCurrentTheme(mode))
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <div>
        <IconButton
          size="small"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          sx={{
            color: theme.palette.text.secondary,
            mr: -1,
            borderRadius: account?.firstName || account?.lastName ? 0 : '50%',
          }}
          onClick={handleMenu}
        >
          {(account?.firstName || account?.lastName) && (
            <Typography
              color={theme.palette.text.primary}
              mt={0.5}
              mx={1}
            >{`${account?.firstName} ${account?.lastName}`}</Typography>
          )}
          <AccountCircleOutlined />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          onClick={handleClose}
          sx={{
            '& .MuiMenu-paper': {
              minWidth: 300,
              borderRadius: 2,
              top: '76px !important',
              right: '25px !important',
            },
          }}
        >
          <Typography mx={2} mt={0.5} mb={1} className="font-semibold">
            {t('current.language')}
          </Typography>
          <Stack direction="row" mx={2} justifyContent="center">
            <Box
              sx={{
                border: `1px solid ${
                  currentLanguage === 'de'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled
                }`,
                '&:hover': {
                  borderColor:
                    currentLanguage === 'de'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  color:
                    currentLanguage === 'de'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                },
                color:
                  currentLanguage === 'de'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled,
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
                p: 1,
                cursor: currentLanguage !== 'de' ? 'pointer' : '',
              }}
              onClick={() => switchLanguage('de')}
            >
              <Typography component="div" sx={{ display: 'inline-flex' }}>
                <Typography mr={1} className="font-semibold">
                  DE
                </Typography>
                Deutsch
              </Typography>
            </Box>
            <Box
              sx={{
                border: `1px solid ${
                  currentLanguage === 'en'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled
                }`,
                '&:hover': {
                  borderColor:
                    currentLanguage === 'en'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  color:
                    currentLanguage === 'en'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                },
                color:
                  currentLanguage === 'en'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                p: 1,
                cursor: currentLanguage !== 'en' ? 'pointer' : '',
              }}
              onClick={() => switchLanguage('en')}
            >
              <Typography component="div" sx={{ display: 'inline-flex' }}>
                <Typography className="font-semibold" mr={1}>
                  ENG
                </Typography>
                English
              </Typography>
            </Box>
          </Stack>
          <Typography mx={2} mt={2} mb={1} className="font-semibold">
            {t('current.theme')}
          </Typography>
          <Stack direction="row" mx={2} mb={2.5} justifyContent="center">
            <Stack
              direction="row"
              sx={{
                border: `1px solid ${
                  currentTheme === 'light'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled
                }`,
                '&:hover': {
                  borderColor:
                    currentTheme === 'light'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  color:
                    currentTheme === 'light'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                },
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
                p: 1,
                cursor: currentTheme !== 'light' ? 'pointer' : '',
                color:
                  currentTheme === 'light'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled,
              }}
              onClick={() => switchTheme('light')}
            >
              <LightModeRounded fontSize="small" sx={{ mt: 0.3 }} />
              <Typography ml={0.5} mt={0.15}>
                {t('theme.light')}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{
                border: `1px solid ${
                  currentTheme === 'dark'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled
                }`,
                '&:hover': {
                  borderColor:
                    currentTheme === 'dark'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  color:
                    currentTheme === 'dark'
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                },
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                p: 1,
                cursor: currentTheme !== 'dark' ? 'pointer' : '',
                color:
                  currentTheme === 'dark'
                    ? theme.palette.primary.main
                    : theme.palette.action.disabled,
              }}
              onClick={() => switchTheme('dark')}
            >
              <DarkModeRounded fontSize="small" sx={{ mt: 0.3 }} />
              <Typography ml={0.5} mt={0.15}>
                {t('theme.dark')}
              </Typography>
            </Stack>
          </Stack>
          <Typography mx={2} mt={2} mb={1} className="font-semibold">
            {t('change.font.size')}
          </Typography>
          <Stack
            direction="row"
            mx={2}
            mb={2.5}
            className="items-center justify-center"
            spacing={2}
          >
            <TextDecreaseRounded />
            <Slider
              aria-label="FontSize"
              defaultValue={htmlFontSize}
              getAriaValueText={(value: number) => `${value}px`}
              valueLabelDisplay="auto"
              valueLabelFormat={(value: number) => `${value}px`}
              shiftStep={2}
              step={2}
              marks
              min={12}
              max={20}
              onChange={(_, value) => {
                dispatch(setHtmlFontSize(32 - value))
              }}
            />
            <TextIncreaseRounded />
          </Stack>
          {account?.isSuperuser && (
            <div style={{ marginBottom: '10px' }}>
              <Divider />
              <Typography mt={2} ml={2} className="font-semibold">
                {t('admin.settings')}
              </Typography>
              <Stack ml={2} mt={2} mb={1}>
                <Typography color="primary" className="font-semibold">
                  PAY_transparency
                </Typography>
              </Stack>
              <MenuItem onClick={navigateToPtImportView}>
                <Typography ml={2}>{t('import.data')}</Typography>
              </MenuItem>
              <Stack ml={2} mt={2} mb={1}>
                <Typography color="primary" className="font-semibold">
                  FAIR_solution
                </Typography>
              </Stack>
              <MenuItem onClick={navigateToImportView}>
                <Typography ml={2}>{t('import.data')}</Typography>
              </MenuItem>
              <MenuItem onClick={navigateToConfigurationView}>
                <Typography ml={2}>{t('configuration.wizard')}</Typography>
              </MenuItem>
            </div>
          )}
          <Divider />
          <Link
            target="_blank"
            href={`https://www.ines-analytics.com/${currentLanguage === 'en' ? 'en/' : ''}blog`}
            sx={{ color: 'text.primary', textDecoration: 'none' }}
          >
            <MenuItem sx={{ mt: 1 }}>
              <Typography>{t('go.to.blog')}</Typography>
            </MenuItem>
          </Link>
          <Link
            target="_blank"
            href={`https://www.ines-analytics.com${currentLanguage === 'en' ? '/en' : ''}`}
            sx={{ color: 'text.primary', textDecoration: 'none' }}
          >
            <MenuItem>
              <Typography mb={1}>INES Analytics Website</Typography>
            </MenuItem>
          </Link>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Typography mt={1}>{t('logout')}</Typography>
          </MenuItem>
        </Menu>
      </div>
    </Stack>
  )
}

export default FsAccountMenu
